import {
  AlertCircle,
  CheckCircle2,
  Gamepad2,
  LockKeyhole,
  Minus,
  Plus,
  ShoppingBag,
  Tag,
  Trash2,
  UserRound,
  X,
} from 'lucide-react'
import { useEffect, useState, type FormEvent } from 'react'

import { cn } from '@/lib/cn'

import { price } from './api'
import { useStore } from './state'
import { packagePath } from './router'
import { Link, Spinner } from './ui'

/** Esc closes whatever sits over the page. */
function useEscape(open: boolean, close: () => void) {
  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && close()
    window.addEventListener('keydown', onKey)
    const overflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = overflow
    }
  }, [open, close])
}

function CodeForm() {
  const basket = useStore((state) => state.basket)
  const busy = useStore((state) => state.busy)
  const applyCode = useStore((state) => state.applyCode)
  const dropCode = useStore((state) => state.dropCode)
  const [kind, setKind] = useState<'coupon' | 'creator'>('coupon')
  const [code, setCode] = useState('')
  if (!basket) return null

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (await applyCode(kind, code)) setCode('')
  }

  const applied = [
    ...basket.coupons.map((each) => ({ kind: 'coupon' as const, code: each.coupon_code })),
    ...(basket.creator_code ? [{ kind: 'creator' as const, code: basket.creator_code }] : []),
  ]

  return (
    <div>
      <div className="mb-2 flex gap-1 text-[12px] font-medium" role="tablist">
        {(
          [
            ['coupon', 'Codice sconto'],
            ['creator', 'Codice creator'],
          ] as const
        ).map(([value, label]) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={kind === value}
            onClick={() => setKind(value)}
            className={cn(
              'rounded-full px-3 py-1 transition',
              kind === value ? 'bg-white/10 text-white' : 'text-mist hover:text-white',
            )}
          >
            {label}
          </button>
        ))}
      </div>
      <form onSubmit={(event) => void submit(event)} className="flex gap-2">
        <input
          value={code}
          onChange={(event) => setCode(event.target.value)}
          placeholder={kind === 'coupon' ? 'Inserisci il codice' : 'Nome del creator'}
          aria-label={kind === 'coupon' ? 'Codice sconto' : 'Codice creator'}
          className="bg-deep placeholder:text-mist/70 focus:border-glow/60 h-10 min-w-0 flex-1 rounded-full border border-white/10 px-4 text-[13px] outline-none"
        />
        <button
          type="submit"
          disabled={busy === 'code' || !code.trim()}
          className="glass inline-flex h-10 items-center gap-1.5 rounded-full px-4 text-[13px] font-medium transition hover:bg-white/10 disabled:opacity-50"
        >
          {busy === 'code' ? <Spinner /> : null}
          Applica
        </button>
      </form>
      {applied.length > 0 && (
        <ul className="mt-3 flex flex-wrap gap-2">
          {applied.map((each) => (
            <li
              key={`${each.kind}:${each.code}`}
              className="bg-blue/15 text-glow ring-glow/20 inline-flex items-center gap-1.5 rounded-full py-1 pr-1.5 pl-3 text-[12px] font-medium ring-1"
            >
              <Tag className="size-3.5" aria-hidden />
              {each.code}
              <button
                type="button"
                onClick={() => void dropCode(each.kind, each.code)}
                className="grid size-5 place-items-center rounded-full hover:bg-white/10"
                aria-label={`Togli ${each.code}`}
              >
                <X className="size-3" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export function BasketDrawer() {
  const open = useStore((state) => state.drawer)
  const setDrawer = useStore((state) => state.setDrawer)
  const basket = useStore((state) => state.basket)
  const busy = useStore((state) => state.busy)
  const remove = useStore((state) => state.remove)
  const setQuantity = useStore((state) => state.setQuantity)
  const checkout = useStore((state) => state.checkout)
  const startOver = useStore((state) => state.startOver)
  const goSignIn = useStore((state) => state.goSignIn)
  const categories = useStore((state) => state.categories)
  const close = () => setDrawer(false)
  useEscape(open, close)
  if (!open) return null

  const lines = basket?.packages ?? []
  const currency = basket?.currency ?? 'EUR'
  // The catalogue's word on each line: its unit price, and whether it takes a quantity.
  const catalogue = new Map(
    (categories ?? []).flatMap((category) => category.packages ?? []).map((pkg) => [pkg.id, pkg]),
  )

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label="Carrello">
      <button
        type="button"
        className="animate-fade absolute inset-0 bg-black/60 backdrop-blur-[2px]"
        onClick={close}
        aria-label="Chiudi il carrello"
      />
      <aside className="animate-slide bg-night absolute inset-y-0 right-0 flex w-full max-w-[420px] flex-col border-l border-white/[0.08] shadow-[-30px_0_80px_-20px_#000]">
        <header className="flex items-center justify-between border-b border-white/[0.07] px-5 py-4">
          <h2 className="font-display flex items-center gap-2.5 text-[16px] font-semibold">
            <ShoppingBag className="text-glow size-[18px]" aria-hidden />
            Carrello
          </h2>
          <button
            type="button"
            onClick={close}
            className="text-mist grid size-9 place-items-center rounded-full hover:bg-white/5 hover:text-white"
            aria-label="Chiudi"
          >
            <X className="size-5" aria-hidden />
          </button>
        </header>

        {basket?.username ? (
          <div className="text-mist flex items-center justify-between gap-3 border-b border-white/[0.07] px-5 py-3 text-[12.5px]">
            <span className="flex min-w-0 items-center gap-2">
              <UserRound className="text-glow size-4 shrink-0" aria-hidden />
              <span className="truncate">
                Consegna a <strong className="text-white">{basket.username}</strong>
              </span>
            </span>
            <button type="button" onClick={startOver} className="shrink-0 hover:text-white">
              Cambia account
            </button>
          </div>
        ) : null}

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {lines.length === 0 ? (
            <div className="py-16 text-center">
              <ShoppingBag className="text-mist mx-auto size-8" aria-hidden />
              <p className="font-display mt-4 text-[15px] font-semibold">Il carrello è vuoto</p>
              <p className="text-mist mt-1.5 text-[13px]">Scegli qualcosa dallo store.</p>
              {basket && !basket.username && (
                <button
                  type="button"
                  onClick={() => void goSignIn()}
                  className="text-glow mt-6 text-[13px] font-medium hover:text-white"
                >
                  Accedi con FiveM
                </button>
              )}
            </div>
          ) : (
            <ul className="divide-y divide-white/[0.06]">
              {lines.map((line) => {
                const working = busy === `line:${line.id}`
                const quantity = line.in_basket.quantity || 1
                const listed = catalogue.get(line.id)
                const counted = line.type !== 'subscription' && !listed?.disable_quantity
                return (
                  <li key={line.id} className="flex gap-3.5 py-4">
                    <div className="bg-deep size-16 shrink-0 overflow-hidden rounded-xl ring-1 ring-white/[0.07]">
                      <img
                        src={line.image ?? '/brand/unity-icon.png'}
                        alt=""
                        className="size-full object-cover"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <Link
                        to={packagePath(line)}
                        onClick={close}
                        className="line-clamp-2 text-[14px] leading-snug font-semibold hover:text-glow"
                      >
                        {line.name}
                      </Link>
                      {line.type === 'subscription' && (
                        <p className="text-mist mt-0.5 text-[12px]">Abbonamento</p>
                      )}
                      <div className="mt-2.5 flex min-h-8 items-center justify-between gap-2">
                        {counted ? (
                          <div className="flex items-center gap-1 rounded-full bg-white/[0.04] p-0.5 ring-1 ring-white/[0.07]">
                            <button
                              type="button"
                              disabled={working}
                              onClick={() => void setQuantity(line.id, quantity - 1)}
                              className="grid size-7 place-items-center rounded-full hover:bg-white/10 disabled:opacity-40"
                              aria-label="Uno in meno"
                            >
                              <Minus className="size-3.5" aria-hidden />
                            </button>
                            <span className="w-6 text-center text-[13px] tabular-nums">
                              {working ? <Spinner className="mx-auto size-3.5" /> : quantity}
                            </span>
                            <button
                              type="button"
                              disabled={working}
                              onClick={() => void setQuantity(line.id, quantity + 1)}
                              className="grid size-7 place-items-center rounded-full hover:bg-white/10 disabled:opacity-40"
                              aria-label="Uno in più"
                            >
                              <Plus className="size-3.5" aria-hidden />
                            </button>
                          </div>
                        ) : (
                          <span />
                        )}
                        <span className="text-[14px] font-semibold tabular-nums">
                          {price(
                            listed ? listed.total_price * quantity : line.in_basket.price,
                            currency,
                          )}
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      disabled={working}
                      onClick={() => void remove(line.id)}
                      className="text-mist hover:text-danger grid size-8 shrink-0 place-items-center self-start rounded-full hover:bg-white/5"
                      aria-label={`Togli ${line.name}`}
                    >
                      <Trash2 className="size-4" aria-hidden />
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        {basket && lines.length > 0 && (
          <footer className="space-y-4 border-t border-white/[0.07] px-5 pt-4 pb-[max(1.25rem,env(safe-area-inset-bottom))]">
            <CodeForm />
            <dl className="space-y-1.5 text-[13px]">
              {basket.sales_tax > 0 && (
                <div className="text-mist flex justify-between">
                  <dt>Tasse</dt>
                  <dd className="tabular-nums">{price(basket.sales_tax, currency)}</dd>
                </div>
              )}
              <div className="flex items-baseline justify-between">
                <dt className="text-fog">Totale</dt>
                <dd className="font-display text-[20px] font-semibold tabular-nums">
                  {price(basket.total_price, currency)}
                </dd>
              </div>
            </dl>
            <button
              type="button"
              disabled={busy === 'checkout'}
              onClick={() => void checkout()}
              className="btn-primary inline-flex h-12 w-full items-center justify-center gap-2 rounded-full text-[15px] font-semibold"
            >
              {busy === 'checkout' ? <Spinner /> : <LockKeyhole className="size-4" aria-hidden />}
              Vai al pagamento
            </button>
            <p className="text-mist text-center text-[11.5px] leading-relaxed">
              Il pagamento è gestito da Tebex, il nostro rivenditore autorizzato. Tasse ed eventuali
              costi del metodo scelto si vedono prima di pagare.
            </p>
          </footer>
        )}
      </aside>
    </div>
  )
}

/** Before the first package goes in: who receives it, through Cfx.re's sign-in. */
export function SignInDialog() {
  const signIn = useStore((state) => state.signIn)
  const closeSignIn = useStore((state) => state.closeSignIn)
  const goSignIn = useStore((state) => state.goSignIn)
  const busy = useStore((state) => state.busy)
  useEscape(signIn !== null, closeSignIn)
  if (!signIn) return null

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="signin-title"
    >
      <button
        type="button"
        className="animate-fade absolute inset-0 bg-black/60 backdrop-blur-[2px]"
        onClick={closeSignIn}
        aria-label="Chiudi"
      />
      <div className="animate-rise bg-night relative w-full max-w-md rounded-3xl border border-white/[0.08] p-7 shadow-[0_40px_120px_-30px_#000]">
        <button
          type="button"
          onClick={closeSignIn}
          className="text-mist absolute top-4 right-4 grid size-9 place-items-center rounded-full hover:bg-white/5 hover:text-white"
          aria-label="Chiudi"
        >
          <X className="size-5" aria-hidden />
        </button>
        <span className="bg-blue/15 text-glow ring-glow/20 grid size-12 place-items-center rounded-2xl ring-1">
          <Gamepad2 className="size-6" aria-hidden />
        </span>
        <h2
          id="signin-title"
          className="font-display mt-5 text-[20px] font-semibold tracking-[-0.01em]"
        >
          Accedi con FiveM
        </h2>
        <p className="text-fog mt-2.5 text-[14px] leading-relaxed">
          {signIn.pkg ? (
            <>
              <strong className="text-white">{signIn.pkg.name}</strong> arriva in gioco sull’account
              Cfx.re con cui accedi adesso. Usa lo stesso account con cui entri in città.
            </>
          ) : (
            'I pacchetti arrivano in gioco sull’account Cfx.re con cui accedi adesso.'
          )}
        </p>
        <button
          type="button"
          disabled={busy === 'signin'}
          onClick={() => void goSignIn()}
          className="btn-primary mt-7 inline-flex h-12 w-full items-center justify-center gap-2 rounded-full text-[15px] font-semibold"
        >
          {busy === 'signin' ? <Spinner /> : <Gamepad2 className="size-4" aria-hidden />}
          Continua su Cfx.re
        </button>
        <p className="text-mist mt-4 text-center text-[12px]">
          Torni qui subito dopo, con il pacchetto già nel carrello.
        </p>
      </div>
    </div>
  )
}

export function Notice() {
  const notice = useStore((state) => state.notice)
  if (!notice) return null
  const Icon = notice.tone === 'ok' ? CheckCircle2 : AlertCircle
  return (
    <div
      className="pointer-events-none fixed inset-x-0 bottom-5 z-[60] flex justify-center px-4"
      role="status"
    >
      <p
        className={cn(
          'animate-rise glass inline-flex max-w-md items-center gap-2.5 rounded-full px-4 py-2.5 text-[13.5px] font-medium shadow-[0_20px_60px_-20px_#000]',
          notice.tone === 'error' && 'ring-danger/40 ring-1',
        )}
      >
        <Icon
          className={cn('size-4 shrink-0', notice.tone === 'ok' ? 'text-live' : 'text-danger')}
          aria-hidden
        />
        {notice.text}
      </p>
    </div>
  )
}
